<template>
  <div class="console-page">
    <div class="console-page__header">
      <div>
        <h1 class="console-page__title">{{ jobTitle }}</h1>
        <p class="console-page__placeholder">
          <span class="console-badge console-badge--small" :class="`console-badge--${job.tone}`">{{ job.statusLabel }}</span>
          <span v-if="hasExecution" class="job-detail__stat">{{ job.completedBeads ?? 0 }}/{{ job.totalBeads ?? executionEntries.length }} steps</span>
          <span class="job-detail__stat">{{ formatRelativeTime(job.updatedAt) }}</span>
        </p>
        <ConsolePollingStatus
          :active="jobPollingActive"
          :busy="pollBusy"
          idle-label="Watching run status every 5s"
          busy-label="Refreshing run status..."
          tone="pending"
        />
      </div>

      <div class="console-page__actions">
        <button type="button" class="button button--ghost" :disabled="loading || actionBusy || stalenessBusy" @click="loadData({ showLoading: true })">
          Refresh
        </button>
        <router-link
          v-if="workspaceId"
          :to="{ name: 'ConsoleWorkspaceDetail', params: { id: workspaceId } }"
          class="button button--ghost"
        >
          Workspace
        </router-link>
      </div>
    </div>

    <section class="console-panel" :class="`console-panel--${job.tone}`">
      <ConsoleListState :loading="loading" :error="error" :is-empty="false" loading-message="Loading run...">
        <div v-if="jobPhaseBanner" class="console-signal" :class="`console-signal--${jobPhaseBanner.tone}`" style="margin-bottom: 16px">
          <span class="console-signal__label">{{ jobPhaseBanner.label }}</span>
          <span class="console-signal__note">{{ jobPhaseBanner.text }}</span>
        </div>

        <div v-if="jobFailureDetail" class="job-detail__failure-block">
          <span class="job-detail__diag-label">Failure details</span>
          <pre class="job-detail__failure-text">{{ jobFailureDetail }}</pre>
        </div>

        <div v-if="job.status === 'pending_approval' && !stalenessInfo" class="job-detail__action-block">
          <p><strong>Plan ready for review.</strong> Approve to begin execution.</p>
          <button type="button" class="button button--primary" :disabled="actionBusy" @click="approvePlan">
            {{ actionBusy ? 'Working...' : 'Approve Plan' }}
          </button>
        </div>

        <div v-if="stalenessInfo" class="job-detail__action-block" data-testid="staleness-decision" role="alert">
          <p class="alert alert--warning">
            <strong>Workspace is behind {{ stalenessInfo.targetBranch || 'the target branch' }}.</strong>
            This plan cannot run yet because this workspace is
            {{ stalenessCommitsLabel }}<template v-if="stalenessInfo.hasConflicts"> and has conflicts</template>.
            Choose how to proceed (recommended: {{ stalenessRecommendationLabel }}).
          </p>
          <div class="button-row">
            <button type="button" :class="decisionButtonClass('new-workspace')" :disabled="stalenessBusy" @click="decideStaleness('new-workspace')">
              {{ stalenessBusy ? 'Working...' : 'Create new workspace' }}
            </button>
            <button v-if="stalenessInfo.canSyncInPlace" type="button" :class="decisionButtonClass('sync')" :disabled="stalenessBusy" @click="decideStaleness('sync')">
              Sync workspace to latest
            </button>
            <button type="button" :class="decisionButtonClass('continue')" :disabled="stalenessBusy" @click="decideStaleness('continue')">
              Continue with current
            </button>
            <button type="button" class="button button--ghost" :disabled="stalenessBusy" @click="dismissStaleness">
              Cancel
            </button>
          </div>
        </div>

        <div v-else-if="job.status === 'waiting_credentials'" class="job-detail__action-block" data-testid="paused-run">
          <!--
            #643: the outer region is still gated on WAITING_CREDENTIALS (the
            lifecycle question — "run halted awaiting external input"), and
            the message + CTA branch on pauseAuthFailure (the categorical
            question — "which external input is missing"). Older payloads or
            an unknown reason fall through to the git-credential default so
            the paused run always shows an actionable affordance. See
            ADR-643.
          -->
          <template v-if="pauseAuthFailure === 'claude_quota'">
            <p data-testid="paused-run-message">
              <strong>Claude usage quota exhausted.</strong>
              This run is paused because the Claude Code account hit its
              usage/weekly quota. Wait for the quota window to reset or
              switch the workspace to a different Claude account, then
              resume this run manually.
            </p>
            <pre v-if="credentialPauseDetail" class="job-detail__failure-text">{{ credentialPauseDetail }}</pre>
            <button
              v-if="resumePhase"
              type="button"
              class="button button--primary"
              data-testid="paused-run-resume"
              :disabled="actionBusy"
              @click="resumeRun"
            >
              {{ actionBusy ? 'Working...' : 'Resume Run' }}
            </button>
            <template v-else>
              <!--
                #643: a claude_* pause with no recorded resumePhase cannot be
                resumed to its prior phase — but the old inert disabled button
                was a dead end. Surface why, and route the operator to the
                workspace to review credentials / restart, instead of leaving
                a permanently-disabled button with no next step.
              -->
              <p data-testid="paused-run-resume-unavailable">
                This run cannot be resumed automatically because it did not
                record which phase to resume to. Open the workspace to review
                its credentials and restart the run from there.
              </p>
              <router-link
                v-if="workspaceId"
                :to="{ name: 'ConsoleWorkspaceDetail', params: { id: workspaceId } }"
                class="button button--primary"
                data-testid="paused-run-resume-unavailable-workspace"
              >
                Open Workspace
              </router-link>
            </template>
          </template>
          <template v-else-if="pauseAuthFailure === 'claude_auth'">
            <p data-testid="paused-run-message">
              <strong>Claude authentication needed.</strong>
              This run is paused because the Claude Code account is
              unauthenticated. Re-authenticate the Claude account on the
              worker (or switch this workspace to a Claude account that is
              signed in), then resume this run manually.
            </p>
            <pre v-if="credentialPauseDetail" class="job-detail__failure-text">{{ credentialPauseDetail }}</pre>
            <button
              v-if="resumePhase"
              type="button"
              class="button button--primary"
              data-testid="paused-run-resume"
              :disabled="actionBusy"
              @click="resumeRun"
            >
              {{ actionBusy ? 'Working...' : 'Resume Run' }}
            </button>
            <template v-else>
              <!--
                #643: a claude_* pause with no recorded resumePhase cannot be
                resumed to its prior phase — but the old inert disabled button
                was a dead end. Surface why, and route the operator to the
                workspace to review credentials / restart, instead of leaving
                a permanently-disabled button with no next step.
              -->
              <p data-testid="paused-run-resume-unavailable">
                This run cannot be resumed automatically because it did not
                record which phase to resume to. Open the workspace to review
                its credentials and restart the run from there.
              </p>
              <router-link
                v-if="workspaceId"
                :to="{ name: 'ConsoleWorkspaceDetail', params: { id: workspaceId } }"
                class="button button--primary"
                data-testid="paused-run-resume-unavailable-workspace"
              >
                Open Workspace
              </router-link>
            </template>
          </template>
          <template v-else>
            <p data-testid="paused-run-message"><strong>Credentials needed.</strong> Update GitHub credentials on the workspace to resume this run.</p>
            <pre v-if="credentialPauseDetail" class="job-detail__failure-text">{{ credentialPauseDetail }}</pre>
            <router-link
              v-if="workspaceId"
              :to="{ name: 'ConsoleWorkspaceDetail', params: { id: workspaceId }, query: { refreshCredentials: '1' } }"
              class="button button--primary"
              data-testid="paused-run-update-credentials"
            >
              Update Credentials on Workspace
            </router-link>
          </template>
        </div>

        <div v-else-if="job.status === 'awaiting_input' && questionRows.length" class="job-detail__action-block">
          <p><strong>Input required.</strong></p>
          <div v-for="question in questionRows" :key="question.index" class="job-detail__question">
            <label class="job-detail__question-label">{{ question.label }}</label>
            <textarea v-model="answers[question.index]" class="form-input" rows="2" />
          </div>
          <button type="button" class="button button--primary" :disabled="actionBusy || !canSubmitAnswers" @click="submitAnswers">
            {{ actionBusy ? 'Working...' : 'Submit' }}
          </button>
        </div>

        <details v-if="preflightPanel" class="console-module" open>
          <summary class="console-module__summary">
            Preflight
            <span class="console-badge console-badge--small" :class="`console-badge--${preflightPanel.verdictTone}`">{{ preflightPanel.verdictLabel }}</span>
          </summary>
          <p class="job-detail__status-line">{{ preflightPanel.summary }}</p>
          <ul v-if="preflightPanel.checks.length" class="job-detail__preflight-list">
            <li v-for="(check, index) in preflightPanel.checks" :key="`${index}-${check.name}`" class="job-detail__preflight-row">
              <span class="console-badge console-badge--small" :class="`console-badge--${check.tone}`">{{ check.statusLabel }}</span>
              <span class="job-detail__preflight-name">{{ check.name }}</span>
              <span class="job-detail__preflight-message">{{ check.message }}</span>
            </li>
          </ul>
          <p v-else class="job-detail__status-line">No individual check details were reported.</p>
        </details>

        <details v-if="showWorkerSetup" class="console-module">
          <summary class="console-module__summary">
            Local Worker Setup
          </summary>
          <p class="job-detail__status-line">
            This run uses the Local Worker (Claude Code) runtime — it executes only while a
            <code>beads-worker</code> process is polling for it. If nothing is progressing, download and run the
            worker below.
          </p>
          <WorkerSetupInstructions :show-heading="false" />
        </details>

        <details v-if="isPlanning" class="console-module" open>
          <summary class="console-module__summary">
            Planner Progress
            <span v-if="plannerStageInfo?.current" class="console-module__count">
              {{ plannerStageInfo.current }} / {{ plannerStageInfo.total }}
            </span>
          </summary>
          <div v-if="plannerStageInfo" class="planner-progress">
            <div class="planner-progress__indicator">
              <span class="job-detail__subheading">{{ plannerStageInfo.display }}</span>
            </div>
            <div v-if="plannerStageInfo.current" class="planner-progress__bar">
              <div
                class="planner-progress__fill"
                :style="{ width: `${(plannerStageInfo.current / plannerStageInfo.total) * 100}%` }"
              />
            </div>
            <p v-if="plannerStageInfo.content" class="job-detail__status-line">
              {{ plannerStageInfo.content }}
            </p>
          </div>
          <div v-else>
            <span class="job-detail__status-line">Starting planner...</span>
          </div>
        </details>

        <details v-if="job.description" class="console-module" :open="isDescriptionShort">
          <summary class="console-module__summary">Request</summary>
          <div class="job-detail__prose" v-html="descriptionHtml" />
        </details>

        <details v-if="hasPlan" class="console-module" :open="job.status === 'pending_approval'">
          <summary class="console-module__summary">
            Plan
            <span class="console-module__count">{{ planEntries.length }} steps</span>
          </summary>
          <div class="job-detail__plan-list">
            <details v-for="entry in planEntries" :key="entry.index" class="job-detail__plan-entry">
              <summary class="job-detail__plan-summary">
                <span class="job-detail__plan-index">#{{ entry.index }}</span>
                <span class="job-detail__plan-title">{{ entry.title }}</span>
                <span v-if="entry.deps" class="job-detail__plan-deps">depends on {{ entry.deps }}</span>
              </summary>
              <div class="job-detail__prose job-detail__prose--nested" v-html="renderMarkdown(entry.task)" />
            </details>
          </div>
        </details>

        <details v-if="hasExecution" class="console-module" :open="isRunning || Boolean(selectedBead)">
          <summary class="console-module__summary">
            Execution
            <span class="console-module__count">{{ executionCompletedCount }}/{{ executionEntries.length }}</span>
          </summary>

          <div v-if="beadsLoading" class="job-detail__status-line">Loading step state...</div>
          <div v-else-if="beadsError" class="job-detail__status-line job-detail__status-line--error">{{ beadsError }}</div>

          <div class="dag">
            <div v-for="phase in dagLayout.phases" :key="phase.depth" class="dag__phase">
              <div class="dag__phase-row">
                <div class="dag__phase-label"><span>Phase {{ phase.depth }}</span></div>
                <div class="dag__nodes" :style="{ 'grid-template-columns': `repeat(${dagLayout.columns}, 1fr)` }">
                  <button
                    v-for="item in phase.items"
                    :key="item.bead.index"
                    type="button"
                    class="dag__node"
                    :class="[`dag__node--${item.bead.tone}`, { 'dag__node--selected': selectedBeadIndex === item.bead.index }]"
                    :style="{ 'grid-column': `${item.colStart} / ${item.colEnd}` }"
                    @click="toggleBead(item.bead.index)"
                  >
                    <span class="dag__node-index">#{{ item.bead.index }}</span>
                    <span class="dag__node-label">{{ item.bead.title }}</span>
                    <span
                      v-if="item.bead.addedAfterApproval"
                      class="console-badge console-badge--small console-badge--attention"
                      title="Added after approval — not part of the approved plan"
                    >added after approval</span>
                    <span v-if="item.bead.usageLabel" class="dag__node-usage">{{ item.bead.usageLabel }}</span>
                    <span class="dag__node-dot" :class="`dag__node-dot--${item.bead.tone}`" />
                  </button>
                </div>
              </div>

              <section v-if="selectedBead && phase.items.some((i) => i.bead.index === selectedBead.index)" class="dag__detail">
                <div class="dag__detail-header">
                  <div>
                    <div class="dag__detail-title-row">
                      <span class="dag__detail-index">#{{ selectedBead.index }}</span>
                      <span class="console-badge console-badge--small" :class="`console-badge--${selectedBead.tone}`">{{ selectedBead.statusLabel }}</span>
                      <span v-if="selectedBead.addedAfterApproval" class="console-badge console-badge--small console-badge--attention">Added after approval</span>
                    </div>
                    <p class="dag__detail-title">{{ selectedBead.title }}</p>
                  </div>
                  <button type="button" class="button button--ghost job-detail__close-button" @click="selectedBeadIndex = null">
                    Close
                  </button>
                </div>

                <div class="job-detail__prose" v-html="renderMarkdown(selectedBead.fullTitle)" />

                <div class="job-detail__diag-grid">
                  <div class="job-detail__diag-item">
                    <span class="job-detail__diag-label">State</span>
                    <span>{{ selectedBead.statusLabel }}</span>
                  </div>
                  <div v-if="selectedBead.role" class="job-detail__diag-item">
                    <span class="job-detail__diag-label">Role</span>
                    <span>{{ selectedBead.role }}</span>
                  </div>
                  <div v-if="selectedBead.beadType" class="job-detail__diag-item">
                    <span class="job-detail__diag-label">Type</span>
                    <span>{{ selectedBead.beadType }}</span>
                  </div>
                  <div v-if="selectedBead.dependsOnLabel" class="job-detail__diag-item">
                    <span class="job-detail__diag-label">Depends on</span>
                    <span>{{ selectedBead.dependsOnLabel }}</span>
                  </div>
                  <div v-if="selectedBead.blockedByLabel" class="job-detail__diag-item">
                    <span class="job-detail__diag-label">Blocking</span>
                    <span>{{ selectedBead.blockedByLabel }}</span>
                  </div>
                  <div v-if="selectedBead.updatedAt" class="job-detail__diag-item">
                    <span class="job-detail__diag-label">Updated</span>
                    <span>{{ formatRelativeTime(selectedBead.updatedAt) }}</span>
                  </div>
                  <div v-if="selectedBead.usage?.model" class="job-detail__diag-item">
                    <span class="job-detail__diag-label">Model</span>
                    <span>{{ selectedBead.usage.model }}</span>
                  </div>
                  <div v-if="selectedBead.usage?.inputTokens" class="job-detail__diag-item">
                    <span class="job-detail__diag-label">Input tokens</span>
                    <span>{{ Number(selectedBead.usage.inputTokens).toLocaleString() }}</span>
                  </div>
                  <div v-if="selectedBead.usage?.outputTokens" class="job-detail__diag-item">
                    <span class="job-detail__diag-label">Output tokens</span>
                    <span>{{ Number(selectedBead.usage.outputTokens).toLocaleString() }}</span>
                  </div>
                  <div v-if="selectedBead.durationLabel" class="job-detail__diag-item">
                    <span class="job-detail__diag-label">Duration</span>
                    <span>{{ selectedBead.durationLabel }}</span>
                  </div>
                  <div v-if="selectedBead.costLabel" class="job-detail__diag-item">
                    <span class="job-detail__diag-label">Cost</span>
                    <span>{{ selectedBead.costLabel }}</span>
                  </div>
                  <div v-if="selectedBead.budgetLabel" class="job-detail__diag-item">
                    <span class="job-detail__diag-label">Budget</span>
                    <span>{{ selectedBead.budgetLabel }}</span>
                  </div>
                </div>

                <div v-if="selectedBead.failureReason" class="job-detail__failure-block">
                  <span class="job-detail__diag-label">Failure</span>
                  <pre class="job-detail__failure-text">{{ selectedBead.failureReason }}</pre>
                </div>

                <div v-if="selectedBead.isStalled" class="job-detail__stalled-note">
                  This step looks stalled based on its last update time and missing live usage.
                </div>

                <div v-if="selectedBead.canKill || selectedBead.canRetry || selectedBead.canSkip" class="job-detail__actions-row">
                  <button
                    v-if="selectedBead.canKill"
                    type="button"
                    class="button button--ghost"
                    :disabled="killBusyIndex === selectedBead.index"
                    @click="killBead(selectedBead)"
                  >
                    {{ killBusyIndex === selectedBead.index ? 'Killing...' : 'Kill' }}
                  </button>
                  <button
                    v-if="selectedBead.canRetry && retryTargetIndex !== selectedBead.index"
                    type="button"
                    class="button button--ghost"
                    @click="startRetry(selectedBead)"
                  >
                    Retry
                  </button>
                  <button
                    v-if="selectedBead.canSkip"
                    type="button"
                    class="button button--ghost"
                    :disabled="skipBusyIndex === selectedBead.index"
                    @click="skipBead(selectedBead)"
                  >
                    {{ skipBusyIndex === selectedBead.index ? 'Skipping...' : 'Skip' }}
                  </button>
                </div>

                <form v-if="retryTargetIndex === selectedBead.index" class="job-detail__retry-form" @submit.prevent="retryBead(selectedBead)">
                  <label class="job-detail__diag-label" for="retry-budget">Budget override (USD)</label>
                  <input id="retry-budget" v-model="retryBudget" type="number" min="0.5" step="0.5" class="form-input job-detail__retry-input" />
                  <div class="job-detail__actions-row">
                    <button type="submit" class="button button--primary" :disabled="retryBusyIndex === selectedBead.index">
                      {{ retryBusyIndex === selectedBead.index ? 'Retrying...' : 'Confirm Retry' }}
                    </button>
                    <button type="button" class="button button--ghost" @click="cancelRetry">Cancel</button>
                  </div>
                </form>

                <p v-if="beadActionError" class="job-detail__status-line job-detail__status-line--error">{{ beadActionError }}</p>

                <div class="job-detail__subsection">
                  <div class="job-detail__subsection-header">
                    <span class="job-detail__diag-label">Output</span>
                    <button
                      v-if="selectedBead.isLive"
                      type="button"
                      class="button button--ghost"
                      :disabled="beadOutputLoading[selectedBead.beadId]"
                      @click="fetchBeadOutput(selectedBead)"
                    >
                      {{ beadOutputLoading[selectedBead.beadId] ? 'Loading...' : 'Reload Output' }}
                    </button>
                  </div>
                  <p v-if="beadOutputErrors[selectedBead.beadId]" class="job-detail__status-line job-detail__status-line--error">
                    Failed to load output: {{ beadOutputErrors[selectedBead.beadId] }}
                  </p>
                  <p v-else-if="selectedBeadOutput && selectedBeadOutput._noOutput" class="job-detail__status-line">No output available.</p>
                  <div v-else-if="selectedBeadOutput" class="job-detail__prose job-detail__prose--boxed" v-html="renderMarkdown(formatDetail(selectedBeadOutput))" />
                  <p v-else class="job-detail__status-line">Select reload to inspect the persisted step output.</p>
                </div>

                <details class="job-detail__conversation" :open="Boolean(selectedConversation)" @toggle="onConversationToggle(selectedBead, $event)">
                  <summary class="job-detail__conversation-summary">Transcript</summary>
                  <div v-if="selectedBead.runId == null" class="job-detail__status-line">Transcript is not available for this step.</div>
                  <div v-else-if="conversationLoading[selectedBead.beadId]" class="job-detail__status-line">Loading transcript...</div>
                  <p v-else-if="conversationErrors[selectedBead.beadId] && conversationErrorKinds[selectedBead.beadId] === 'unavailable' && isBeadInProgress(selectedBead)" class="job-detail__status-line">
                    Transcript will be available when this step completes.
                  </p>
                  <p v-else-if="conversationErrors[selectedBead.beadId]" class="job-detail__status-line job-detail__status-line--error">
                    Failed to load transcript: {{ conversationErrors[selectedBead.beadId] }}
                  </p>
                  <div v-else-if="selectedConversation" class="job-detail__conversation-body">
                    <div v-if="selectedConversation.systemPrompt" class="job-detail__conversation-system">
                      <div class="job-detail__diag-label">System Prompt</div>
                      <pre class="job-detail__conversation-pre">{{ selectedConversation.systemPrompt }}</pre>
                    </div>
                    <div v-for="(message, index) in selectedConversation.messages" :key="`${selectedBead.beadId}-${index}`" class="job-detail__conversation-message" :class="`job-detail__conversation-message--${message.role}`">
                      <div class="job-detail__conversation-role">{{ message.role }}</div>
                      <pre class="job-detail__conversation-pre">{{ message.content }}</pre>
                    </div>
                    <!-- #763 (round-3): a VALID but empty conversation renders an
                         explicit empty state, never a silent blank panel. -->
                    <p v-if="!selectedConversation.messages.length" class="job-detail__status-line">No messages recorded for this step.</p>
                  </div>
                  <div v-else class="job-detail__status-line">Open this section to load the transcript.</div>
                </details>
              </section>
            </div>
          </div>

          <p v-if="!selectedBead" class="job-detail__status-line">Select a step to inspect controls, output, transcript, and live diagnostics.</p>
        </details>

        <details v-if="hasResults || isSynthesizing" class="console-module" :open="isCompleted || isSynthesizing">
          <summary class="console-module__summary">Results</summary>
          <div v-if="isSynthesizing && !hasResults" class="job-detail__status-line">
            Synthesizing results from completed steps...
          </div>
          <div v-if="summaryText" class="job-detail__prose" v-html="summaryHtml" />

          <div v-if="acceptanceRows.length" class="job-detail__acceptance">
            <h4 class="job-detail__subheading">Acceptance</h4>
            <div v-for="row in acceptanceRows" :key="row.item" class="job-detail__acceptance-row">
              <span class="console-badge console-badge--small" :class="`console-badge--${row.tone}`">{{ row.label }}</span>
              <span>{{ row.item }}</span>
            </div>
          </div>

          <div v-if="reviewFindingRows.length" class="job-detail__findings">
            <h4 class="job-detail__subheading">Review Findings</h4>
            <div v-for="row in reviewFindingRows" :key="row.key" class="job-detail__finding-row">
              <span class="console-badge console-badge--small" :class="`console-badge--${row.tone}`">{{ row.severity }}</span>
              <div class="job-detail__finding-body">
                <div class="job-detail__finding-meta">
                  <span class="job-detail__finding-location">{{ row.location }}</span>
                  <span v-if="row.rule" class="job-detail__finding-rule">{{ row.rule }}</span>
                </div>
                <div class="job-detail__finding-description">{{ row.description }}</div>
              </div>
            </div>
          </div>
        </details>

        <details v-if="hasDiagnostics" class="console-module">
          <summary class="console-module__summary">Diagnostics</summary>
          <div class="job-detail__diagnostics-grid">
            <section v-if="analyticsTroubleSpots.length" class="job-detail__diagnostic-block">
              <h4 class="job-detail__subheading">Trouble Spots</h4>
              <ul class="job-detail__diagnostic-list">
                <li v-for="(spot, index) in analyticsTroubleSpots" :key="`spot-${index}`">{{ formatDiagnosticLabel(spot) }}</li>
              </ul>
            </section>

            <section v-if="analyticsGuidanceGaps.length" class="job-detail__diagnostic-block">
              <h4 class="job-detail__subheading">Guidance Gaps</h4>
              <ul class="job-detail__diagnostic-list">
                <li v-for="(gap, index) in analyticsGuidanceGaps" :key="`gap-${index}`">{{ formatDiagnosticLabel(gap) }}</li>
              </ul>
            </section>

            <section v-if="analyticsStandardsEvidence.length" class="job-detail__diagnostic-block">
              <h4 class="job-detail__subheading">Evidence Used</h4>
              <ul class="job-detail__diagnostic-list">
                <li v-for="(item, index) in analyticsStandardsEvidence" :key="`evidence-${index}`">{{ formatEvidenceLabel(item) }}</li>
              </ul>
            </section>
          </div>
        </details>

        <details class="console-module">
          <summary class="console-module__summary">Details</summary>
          <dl class="job-detail__meta-list">
            <div><dt>Run ID</dt><dd class="job-detail__mono">{{ jobId }}</dd></div>
            <div><dt>Workspace</dt><dd>{{ workspaceTitle }}</dd></div>
            <div><dt>Created</dt><dd>{{ formatRelativeTime(job.createdAt) }}</dd></div>
            <div><dt>Updated</dt><dd>{{ formatRelativeTime(job.updatedAt) }}</dd></div>
            <div v-if="job.runtime"><dt>Runtime</dt><dd>{{ job.runtime }}</dd></div>
          </dl>

          <button type="button" class="button button--ghost job-detail__delete" :disabled="actionBusy || stalenessBusy" @click="deleteJob">
            Delete Run
          </button>
        </details>

        <p v-if="actionMessage" class="job-detail__message">{{ actionMessage }}</p>
      </ConsoleListState>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ConsoleListState from '@/components/ConsoleListState.vue'
import ConsolePollingStatus from '@/components/ConsolePollingStatus.vue'
import WorkerSetupInstructions from '@/components/WorkerSetupInstructions.vue'
import { formatJobStatus, isActiveJobStatus, normalizeJob, summarizeJobTitle } from '@/composables/useJobPresentation.js'
import { renderMarkdown } from '@/composables/useMarkdown.js'
import { useOrchestratorRequest } from '@/composables/useOrchestratorRequest.js'
import { formatRelativeTime } from '@/composables/useRelativeTime.js'
import { useFrontendLogger } from '@/services/FrontendLogger'
import { appWorkspaceTaskClient } from '@/services/WorkspaceTaskClient.js'

const route = useRoute()
const router = useRouter()
const { request, requestJson } = useOrchestratorRequest()
const logger = useFrontendLogger().child('[ConsoleJobDetail]')
const POLL_INTERVAL_MS = 5000
const MAX_POLL_ERRORS = 5
const POLL_STATUSES = new Set([
  'planning',
  'awaiting_input',
  'pending_approval',
  'approved',
  'running',
  'delivering',
  'waiting_credentials',
  'synthesizing',
])

const jobId = route.params.id

const loading = ref(true)
const error = ref('')
const actionBusy = ref(false)
const actionMessage = ref('')
// #557: staleness decision surfaced when an approval cannot activate execution
// because the workspace baseline is behind the target branch.
const stalenessInfo = ref(null)
const stalenessBusy = ref(false)
// #557: marker for the upfront staleness check on entry into pending_approval —
// flips to true on the first transition and resets when the run leaves
// pending_approval, so polling ticks do not respawn the task.
const stalenessCheckInitiated = ref(false)
// #574: set when the operator explicitly dismisses the panel — an in-flight
// upfront check must not revive a dismissed panel when it resolves.
const stalenessDismissed = ref(false)
// #574: incremented each time an upfront check starts. A check captures the
// generation at start and only applies its result if it is still current — so a
// check from a prior pending_approval entry that resolves after a leave/re-entry
// cannot apply its stale result (or breadcrumb) over the current entry's check.
const stalenessCheckGeneration = ref(0)
const stalenessRecommendationLabel = computed(() => {
  const rec = stalenessInfo.value?.recommendation
  if (rec === 'new-workspace') return 'create a new workspace'
  if (rec === 'sync') return 'sync to latest'
  if (rec === 'continue') return 'continue with current'
  return rec || 'create a new workspace'
})
const stalenessCommitsLabel = computed(() => {
  const n = Number(stalenessInfo.value?.commitsBehind ?? 0)
  return `${n} commit${n === 1 ? '' : 's'} behind`
})
// Highlight the server-recommended decision as primary (defaults to new-workspace).
function decisionButtonClass(decision) {
  const rec = stalenessInfo.value?.recommendation || 'new-workspace'
  return rec === decision ? 'button button--primary' : 'button'
}

const job = ref(normalizeJob({ id: jobId, phase: 'PLANNING', createdAt: new Date().toISOString() }))
const results = ref(null)
const beads = ref([])
const beadsLoading = ref(false)
const beadsError = ref('')

const selectedBeadIndex = ref(null)
const answers = ref([])

const beadOutputs = ref({})
const beadOutputLoading = ref({})
const beadOutputErrors = ref({})

const conversations = ref({})
const conversationLoading = ref({})
const conversationErrors = ref({})
// #763 (round-3): classify each conversation error so the in-progress "not
// ready" mask applies ONLY to the expected "transcript not available yet" case
// (the endpoint returned a non-OK status), never to a real malformed/CORS/
// download load failure — which must be surfaced even while a bead is running.
// 'unavailable' = endpoint said the transcript is not ready; 'load-failed' =
// the response arrived but could not be resolved/validated.
const conversationErrorKinds = ref({})

const killBusyIndex = ref(null)
const skipBusyIndex = ref(null)
const retryBusyIndex = ref(null)
const retryTargetIndex = ref(null)
const retryBudget = ref('')
const beadActionError = ref('')
const pollBusy = ref(false)
let pollTimer = null
let pollErrorCount = 0

const workspace = computed(() => job.value.workspace ?? {})
const workspaceId = computed(() => job.value.workspaceId ?? workspace.value.id ?? '')
const workspaceTitle = computed(() => workspace.value.name || workspaceId.value || 'Unknown')
const jobTitle = computed(() => summarizeJobTitle(job.value.description, jobId, 60))
const jobPollingActive = computed(() => pollBusy.value || isPollableJobStatus(job.value.status))

// #509: planner stages keyed by the server-provided plannerPhase, so the UI
// reflects the live stage without polling the per-bead progress endpoint
// (which the worker writes under phase-specific bead ids, not planner-1).
const PLANNER_STAGES = [
  { phase: 'bootstrap', display: 'Reading requirements' },
  { phase: 's2', display: 'Mapping dependencies' },
  { phase: 's3', display: 'Drafting steps' },
  { phase: 's4', display: 'Grounding files & finalizing the plan' },
]

const isPlanning = computed(() => job.value.status === 'planning')
const plannerStageInfo = computed(() => {
  // plannerPhase is 'bootstrap' (lowercase) + 'S2'/'S3'/'S4' (uppercase, per the
  // server stage contract); normalize case so PLANNER_STAGES lookups match.
  const phase = (job.value.plannerPhase || '').replace(/^planner-/, '').toLowerCase()
  if (!phase) return null
  const stageIndex = PLANNER_STAGES.findIndex((s) => s.phase === phase)
  const display = stageIndex >= 0 ? PLANNER_STAGES[stageIndex].display : `Planning (${phase})`
  return {
    current: stageIndex >= 0 ? stageIndex + 1 : PLANNER_STAGES.length,
    total: PLANNER_STAGES.length,
    display,
    content: display,
  }
})

const isSynthesizing = computed(() => job.value.status === 'synthesizing')
const isRunning = computed(() => ['running', 'delivering', 'synthesizing', 'approved'].includes(job.value.status))
// #506: surface the worker download + run instructions on the run detail. A
// 'claude-code' run is claimed and executed by a polling beads-worker for the
// planner job, the bead jobs, and synthesis, and it resumes from claim-releasing
// pauses (awaiting_input drops planClaimedAt; waiting_credentials releases the
// claim) only via a fresh worker poll-claim with no queue fallback. So every
// active run status can need that worker EXCEPT pending_approval, which waits on
// a human, not a worker — hence: any active status except pending_approval
// (reusing the centralized isActiveJobStatus predicate). It renders collapsed
// and is NOT auto-expanded: reliably detecting "no worker is polling" from the
// frontend would require guessing at claim liveness (planClaimedAt /
// runningBeads / synthesisClaimedAt), which is unreliable — so the section is
// discoverable rather than driven by a fragile heuristic.
const isLocalWorkerRun = computed(() => job.value.runtime === 'claude-code')
const showWorkerSetup = computed(
  () => isLocalWorkerRun.value
    && isActiveJobStatus(job.value.status)
    && job.value.status !== 'pending_approval',
)
const hasExecutionFailureDetail = computed(() => executionEntries.value.some((entry) => {
  return typeof entry.failureReason === 'string' && entry.failureReason.trim().length > 0
}))
const jobFailureDetail = computed(() => {
  if (job.value.status !== 'failed' || hasExecutionFailureDetail.value) return ''
  return typeof job.value.error === 'string' ? job.value.error.trim() : ''
})

const credentialPauseDetail = computed(() => {
  if (job.value.status !== 'waiting_credentials') return ''
  return typeof job.value.error === 'string' ? job.value.error.trim() : ''
})

// #643: reason-keyed discriminator the paused-run region branches on. The
// backend surfaces this as an explicit typed field on the /jobs/{id} payload
// (enrichJobDetail in server/src/lambda/handler-jobs.ts). The co-located
// types.ts documents the union values and their meaning for REFERENCE ONLY —
// this SFC is plain JS (checkJs is off), so those types are NOT enforced
// against this component; they mirror the server contract for readers, not as
// a compile-time guard. An older payload without the field or an unknown
// string falls through to the github-credential default in the template —
// ADR-643 records that graceful degradation is a required consequence of the
// reason-keyed UI branching design.
const KNOWN_PAUSE_REASONS = new Set(['git_expired', 'git_insufficient_scope', 'claude_auth', 'claude_quota'])
const pauseAuthFailure = computed(() => {
  const value = job.value.pauseAuthFailure
  return typeof value === 'string' && KNOWN_PAUSE_REASONS.has(value) ? value : null
})

// #643: phase the job resumes TO. The manual-resume PATCH sets this as the
// new job phase — mirrors the auto-resume patch in WorkerService
// resumeWaitingCredentialsJobsForWorkspace. Null when the payload omits it
// (older payload or the job is not paused); the Resume Run button stays
// disabled in that case rather than firing a PATCH that would leave the job
// in an ambiguous state.
const KNOWN_RESUME_PHASES = new Set(['PLANNING', 'EXECUTING', 'DELIVERING', 'SYNTHESIZING'])
const resumePhase = computed(() => {
  const value = job.value.resumePhase
  return typeof value === 'string' && KNOWN_RESUME_PHASES.has(value) ? value : null
})

const jobPhaseBanner = computed(() => {
  const s = job.value.status
  if (s === 'planning') return { label: 'Planning', text: 'Planner is building the execution plan.', tone: 'active' }
  if (s === 'awaiting_input') return { label: 'Input Required', text: 'Waiting for your input before planning can continue.', tone: 'attention' }
  if (s === 'pending_approval') return { label: 'Review', text: 'Plan is ready for your review and approval.', tone: 'attention' }
  if (s === 'approved' || s === 'running') {
    const done = job.value.completedBeads ?? 0
    const total = job.value.totalBeads ?? 0
    return { label: 'Executing', text: `Running steps — ${done}/${total} complete.`, tone: 'active' }
  }
  if (s === 'delivering') return { label: 'Delivering', text: 'All steps complete. Delivering results to the target branch.', tone: 'active' }
  if (s === 'waiting_credentials') return { label: 'Credentials Needed', text: 'This run is paused waiting for updated workspace credentials.', tone: 'attention' }
  if (s === 'synthesizing') return { label: 'Synthesizing', text: 'All steps complete. Synthesizing results.', tone: 'active' }
  if (s === 'completed') return { label: 'Complete', text: 'Run completed successfully.', tone: 'ready' }
  if (s === 'failed') return { label: 'Failed', text: 'Run failed.', tone: 'failed' }
  return null
})
// #489: surface the preflight verdict on the run detail. The server runs
// preflight before dispatching to the planner and persists the result on the
// job record (handler-jobs.ts), but the UI never showed it — so a degraded
// 'llm-runtime-auth' check ("worker may be offline") left runs wedged in
// PLANNING for 40+ minutes with no on-screen signal. The panel renders only
// when the verdict is not 'pass' (i.e. degraded or blocked) and stays expanded:
// it is the first thing the operator should see when a run is not progressing.
// Verdict values are 'pass' | 'degraded' | 'blocked'; per-check status values
// are 'passed' | 'degraded' | 'failed' | 'skipped' (preflight-types.ts).
const PREFLIGHT_STATUS_LABELS = {
  passed: 'Passed',
  failed: 'Failed',
  degraded: 'Degraded',
  skipped: 'Skipped',
}
function preflightCheckTone(status) {
  if (status === 'passed') return 'ready'
  if (status === 'degraded') return 'attention'
  if (status === 'failed') return 'failed'
  return 'neutral'
}
const preflightPanel = computed(() => {
  const pf = job.value.preflight
  if (!pf || typeof pf !== 'object' || Array.isArray(pf)) return null
  // Verdict is a closed set: 'pass' | 'degraded' | 'blocked' (preflight-types.ts).
  // Gate strictly on the two problem verdicts — 'pass' and any malformed verdict
  // (e.g. a check-status value like 'passed' mistakenly in the verdict field)
  // render nothing, rather than a misleading attention-toned panel.
  const verdict = typeof pf.verdict === 'string' ? pf.verdict : ''
  if (verdict !== 'degraded' && verdict !== 'blocked') return null
  const checks = Array.isArray(pf.checks) ? pf.checks : []
  const mappedChecks = checks.map((check) => ({
    name: typeof check?.name === 'string' ? check.name : 'unknown',
    tone: preflightCheckTone(check?.status),
    statusLabel: PREFLIGHT_STATUS_LABELS[check?.status] ?? (check?.status || 'unknown'),
    message: typeof check?.message === 'string' ? check.message : '',
  }))
  // The lead-in is keyed ONLY on the verdict — the one distinction that is always
  // true regardless of which checks failed: 'blocked' is persisted as ERRORED and
  // the run stops (handler-jobs.ts), 'degraded' proceeds in degraded mode.
  // Per-check remediation (worker offline vs `claude auth login` vs branch
  // missing) is authored authoritatively by the server in each check's own
  // message (PreflightService), so the panel surfaces that message verbatim
  // rather than re-deriving guidance here — frontend-authored copy drifts from
  // the contract and mis-advises (e.g. a 'failed' auth check needs a login, not a
  // worker restart).
  return {
    verdict,
    verdictLabel: verdict === 'blocked' ? 'Blocked' : 'Degraded',
    verdictTone: verdict === 'blocked' ? 'failed' : 'attention',
    summary: verdict === 'blocked'
      ? 'This run was blocked before planning and stopped. Resolve the failed preflight checks below, then resubmit.'
      : 'Planning will proceed, but these preflight checks are degraded. Review each one below — its message has the specifics.',
    checks: mappedChecks,
  }
})
const isCompleted = computed(() => ['completed', 'failed', 'cancelled'].includes(job.value.status))
const isDescriptionShort = computed(() => (job.value.description?.length ?? 0) < 200)

const hasPlan = computed(() => Array.isArray(job.value.plan) && job.value.plan.length > 0)
const summaryText = computed(() => {
  const summary = results.value?.summary
  return typeof summary === 'string' ? summary.trim() : ''
})
const descriptionHtml = computed(() => renderMarkdown(job.value.description ?? ''))
const summaryHtml = computed(() => renderMarkdown(summaryText.value))

const analytics = computed(() => {
  const value = results.value?.analytics
  return value && typeof value === 'object' ? value : null
})
const analyticsStandardsEvidence = computed(() => Array.isArray(analytics.value?.evidence_used?.standards) ? analytics.value.evidence_used.standards : [])
const analyticsGuidanceGaps = computed(() => Array.isArray(analytics.value?.guidance_gaps) ? analytics.value.guidance_gaps : [])
const analyticsTroubleSpots = computed(() => Array.isArray(analytics.value?.trouble_spots) ? analytics.value.trouble_spots : [])
const hasDiagnostics = computed(() => analyticsStandardsEvidence.value.length > 0 || analyticsGuidanceGaps.value.length > 0 || analyticsTroubleSpots.value.length > 0)

const planEntries = computed(() => {
  const plan = Array.isArray(job.value.plan) ? job.value.plan : []
  return plan
    .filter((entry) => typeof entry?.index === 'number')
    .map((entry) => ({
      index: entry.index,
      title: summarizeJobTitle(typeof entry.task === 'string' ? entry.task : '', `#${entry.index}`, 60),
      task: typeof entry.task === 'string' ? entry.task : `Step #${entry.index}`,
      deps: Array.isArray(entry.depends_on) && entry.depends_on.length ? entry.depends_on.map((dep) => `#${dep}`).join(', ') : '',
    }))
    .sort((a, b) => a.index - b.index)
})

const executionEntries = computed(() => {
  const byIndex = new Map()
  // #515: steps whose index is not in the operator-approved plan were appended
  // after approval (e.g. an auto-injected PR bead). Flag them from real data so
  // the execution view can mark them — no approval-gate prediction needed.
  // Guard: only when an approved plan was actually loaded. Fixed-pipeline jobs
  // store the plan as an artifact, not in the job record, so job.plan is absent;
  // an empty set must flag nothing rather than mislabel every step.
  const approvedIndexSet = new Set(planEntries.value.map((entry) => entry.index))

  for (const planEntry of planEntries.value) {
    byIndex.set(planEntry.index, {
      index: planEntry.index,
      beadId: String(planEntry.index),
      title: planEntry.title,
      fullTitle: planEntry.task,
      prompt: planEntry.task,
      status: 'planned',
      statusLabel: 'planned',
      tone: 'neutral',
      dependsOn: parseDependsOn(planEntry.deps),
      dependsOnLabel: planEntry.deps,
      blockedBy: [],
      blockedByLabel: '',
      isLive: false,
      canKill: false,
      canRetry: false,
      canSkip: false,
      usageLabel: '',
      durationLabel: '',
      costLabel: '',
      budgetLabel: '',
      runId: null,
      isStalled: false,
    })
  }

  for (const bead of beads.value) {
    const existing = byIndex.get(bead.index) ?? {}
    byIndex.set(bead.index, {
      ...existing,
      ...bead,
      title: bead.title,
      fullTitle: bead.fullTitle,
      prompt: bead.prompt,
      dependsOnLabel: bead.dependsOn.length ? bead.dependsOn.map((dep) => `#${dep}`).join(', ') : '',
      blockedByLabel: bead.blockedBy.length ? bead.blockedBy.map((dep) => `#${dep}`).join(', ') : '',
      usageLabel: formatTokens(bead.usage),
      durationLabel: formatDuration(bead.usage?.durationMs),
      costLabel: formatCurrency(bead.usage?.estimatedCostUSD),
      budgetLabel: formatCurrency(bead.maxCostUSD),
      isStalled: detectStalledBead(bead),
    })
  }

  return [...byIndex.values()]
    .map((entry) => ({ ...entry, addedAfterApproval: approvedIndexSet.size > 0 && !approvedIndexSet.has(entry.index) }))
    .sort((a, b) => a.index - b.index)
})

const executionCompletedCount = computed(() => executionEntries.value.filter((entry) => entry.status === 'completed').length)
const hasExecution = computed(() => executionEntries.value.length > 0 && job.value.status !== 'pending_approval')
const hasResults = computed(() => summaryText.value || acceptanceRows.value.length > 0 || reviewFindingRows.value.length > 0)

const selectedBead = computed(() => {
  if (selectedBeadIndex.value == null) return null
  return executionEntries.value.find((entry) => entry.index === selectedBeadIndex.value) ?? null
})

const selectedBeadOutput = computed(() => {
  if (!selectedBead.value) return null
  const key = selectedBead.value.beadId
  if (beadOutputs.value[key] !== undefined) return beadOutputs.value[key]
  if (selectedBead.value.outputInline !== undefined && selectedBead.value.outputInline !== null) return selectedBead.value.outputInline
  const beadOutput = Array.isArray(results.value?.beadOutputs)
    ? results.value.beadOutputs.find((output) => String(output.beadId ?? output.index ?? '') === key)
    : null
  return beadOutput ?? null
})

const selectedConversation = computed(() => {
  if (!selectedBead.value) return null
  return conversations.value[selectedBead.value.beadId] ?? null
})

const dagLayout = computed(() => {
  const entries = executionEntries.value
  if (!entries.length) return { columns: 1, phases: [] }

  const depthMap = new Map()
  function getDepth(entry) {
    if (depthMap.has(entry.index)) return depthMap.get(entry.index)
    if (!entry.dependsOn.length) {
      depthMap.set(entry.index, 0)
      return 0
    }
    let maxDep = 0
    for (const dep of entry.dependsOn) {
      const depEntry = entries.find((candidate) => candidate.index === dep)
      if (depEntry) maxDep = Math.max(maxDep, getDepth(depEntry) + 1)
    }
    depthMap.set(entry.index, maxDep)
    return maxDep
  }
  for (const entry of entries) getDepth(entry)

  const depthGroups = new Map()
  for (const entry of entries) {
    const depth = depthMap.get(entry.index)
    if (!depthGroups.has(depth)) depthGroups.set(depth, [])
    depthGroups.get(depth).push(entry)
  }
  const depths = [...depthGroups.keys()].sort((a, b) => a - b)
  let columns = 1
  for (const group of depthGroups.values()) {
    columns = Math.max(columns, group.length)
  }

  const layout = new Map()
  for (const depth of depths) {
    const group = [...depthGroups.get(depth)].sort((a, b) => a.index - b.index)
    if (depth === 0) {
      for (let i = 0; i < group.length; i += 1) {
        const colStart = Math.round((i * columns) / group.length) + 1
        const colEnd = Math.max(colStart + 1, Math.round(((i + 1) * columns) / group.length) + 1)
        layout.set(group[i].index, { colStart, colEnd })
      }
      continue
    }

    const ranged = group.map((entry) => {
      let minCol = columns + 1
      let maxCol = 0
      let sumCenter = 0
      let parentCount = 0
      for (const dep of entry.dependsOn) {
        const placement = layout.get(dep)
        if (!placement) continue
        minCol = Math.min(minCol, placement.colStart)
        maxCol = Math.max(maxCol, placement.colEnd)
        sumCenter += (placement.colStart + placement.colEnd) / 2
        parentCount += 1
      }
      if (minCol > maxCol) {
        minCol = 1
        maxCol = columns + 1
      }
      return {
        entry,
        minCol,
        maxCol,
        barycenter: parentCount > 0 ? sumCenter / parentCount : (columns + 1) / 2,
      }
    }).sort((a, b) => a.barycenter - b.barycenter)

    for (let i = 0; i < ranged.length; i += 1) {
      const colStart = Math.round(ranged[i].minCol + ((ranged[i].maxCol - ranged[i].minCol) * i) / ranged.length)
      const colEnd = Math.max(colStart + 1, Math.round(ranged[i].minCol + ((ranged[i].maxCol - ranged[i].minCol) * (i + 1)) / ranged.length))
      layout.set(ranged[i].entry.index, { colStart, colEnd })
    }
  }

  return {
    columns,
    phases: depths.map((depth) => ({
      depth,
      items: [...depthGroups.get(depth)]
        .sort((a, b) => (layout.get(a.index)?.colStart ?? 0) - (layout.get(b.index)?.colStart ?? 0))
        .map((entry) => ({
          bead: entry,
          ...layout.get(entry.index),
        })),
    })),
  }
})

const questionRows = computed(() => {
  const questions = Array.isArray(job.value.questions) ? job.value.questions : []
  const answerMap = normalizeAnswerMap(job.value.answers)
  return questions.map((label, index) => ({ index, label, answer: answerMap.get(String(index)) || '' }))
})

const canSubmitAnswers = computed(() => {
  return questionRows.value.length > 0 && questionRows.value.every((row) => {
    const value = answers.value[row.index]
    return typeof value === 'string' && value.trim().length > 0
  })
})

const acceptanceRows = computed(() => {
  const closure = Array.isArray(results.value?.acceptanceClosure) ? results.value.acceptanceClosure : []
  return closure
    .map((entry) => ({
      item: normalizeAcceptanceItem(entry?.item),
      label: formatJobStatus(entry?.status || 'unreported'),
      tone: closureTone(entry?.status),
    }))
    .filter((entry) => entry.item)
})

function parseDependsOn(value) {
  if (!value) return []
  return String(value)
    .split(',')
    .map((item) => Number(item.replace('#', '').trim()))
    .filter((item) => Number.isFinite(item))
}

function normalizeAcceptanceItem(value) {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : ''
}

function normalizeAnswerMap(value) {
  if (!value || typeof value !== 'object') return new Map()
  const entries = Array.isArray(value) ? value.map((answer, index) => [String(index), answer]) : Object.entries(value)
  return new Map(entries.filter(([, answer]) => typeof answer === 'string').map(([key, answer]) => [String(key), answer.trim()]))
}

function closureTone(status) {
  if (status === 'satisfied') return 'ready'
  if (status === 'partial') return 'pending'
  if (status === 'unmet') return 'failed'
  return 'neutral'
}

// Severity → badge tone. Mirrors shared/src/review-policy.ts REVIEW_SEVERITIES
// (BLOCKING/HIGH/MEDIUM/SUGGESTION) without importing shared/ — the console does
// not depend on @beads/shared (ADR-661 rev2). Unknown/forward-compat severities
// fall back to 'neutral' rather than being dropped, so a new severity level is
// still visible (just untoned) instead of silently disappearing.
const FINDING_SEVERITY_TONES = {
  BLOCKING: 'failed',
  HIGH: 'attention',
  MEDIUM: 'pending',
  SUGGESTION: 'neutral',
}

function findingSeverityTone(severity) {
  return FINDING_SEVERITY_TONES[severity] ?? 'neutral'
}

function normalizeFindingLocation(finding) {
  const file = typeof finding.file === 'string' ? finding.file.trim() : ''
  const line = typeof finding.line === 'number' || typeof finding.line === 'string' ? String(finding.line).trim() : ''
  if (file && line) return `${file}:${line}`
  return file || line || 'unknown location'
}

// ADR-661 Slice 1 — surface only. Displays the finding's own authored `severity`
// as-is; deterministic severity calibration is Slice 2 (shared/, not the console).
// Projects only the known ReviewFinding fields (severity/file/line/rule/description)
// so malformed or extra fields never reach the DOM (rev2 SHOULD-FIX).
const reviewFindingRows = computed(() => {
  const outputs = Array.isArray(results.value?.beadOutputs) ? results.value.beadOutputs : []
  const rows = []
  outputs.forEach((beadOutput, beadOutputIndex) => {
    if (beadOutput?.type !== 'review') return
    const findings = beadOutput?.output?.reviewFindings
    if (!Array.isArray(findings)) return
    findings.forEach((finding, findingIndex) => {
      if (!finding || typeof finding !== 'object') return
      const severity = typeof finding.severity === 'string' ? finding.severity : null
      const description = typeof finding.description === 'string' ? finding.description.trim() : ''
      if (!severity || !description) return
      rows.push({
        key: `${beadOutput.index ?? beadOutputIndex}-${findingIndex}`,
        severity,
        tone: findingSeverityTone(severity),
        location: normalizeFindingLocation(finding),
        rule: typeof finding.rule === 'string' ? finding.rule.trim() : '',
        description,
      })
    })
  })
  return rows
})

function isBeadInProgress(bead) {
  const state = (bead?.state || bead?.status || '').toUpperCase()
  return state === 'RUNNING' || state === 'CLAIMED' || state === 'READY' || state === 'WAITING'
}

function beadStateToStatus(state) {
  const map = {
    WAITING: 'queued',
    READY: 'queued',
    RUNNING: 'in_progress',
    COMPLETED: 'completed',
    FAILED: 'failed',
    BLOCKED: 'blocked',
    SKIPPED: 'skipped',
  }
  return map[state] ?? (String(state || '').toLowerCase() || 'unknown')
}

function beadTone(status) {
  if (status === 'completed') return 'ready'
  if (status === 'in_progress') return 'active'
  if (status === 'failed' || status === 'blocked') return 'failed'
  if (status === 'queued' || status === 'skipped') return 'pending'
  return 'neutral'
}

function normalizeBeadRecord(raw) {
  const index = Number(raw.index)
  const status = beadStateToStatus(raw.state)
  const fullTitle = typeof raw.task === 'string' && raw.task.trim() ? raw.task : `${raw.type ?? 'step'} #${index}`
  const dependsOn = Array.isArray(raw.depends_on) ? raw.depends_on.map((item) => Number(item)).filter((item) => Number.isFinite(item)) : []
  const blockedBy = Array.isArray(raw.blocks) ? raw.blocks.map((item) => String(item)) : []
  const beadId = String(index)
  const artifactBeadId = `${jobId}-${index}`
  const explicitRunId = raw.runId ?? raw.currentRunId ?? raw.run_id ?? raw.current_run_id
  const runCount = Number(raw.run_count)
  const runId = Number.isFinite(Number(explicitRunId))
    ? Number(explicitRunId)
    : Number.isFinite(runCount) && runCount > 0
      ? runCount - 1
      : null
  const failureReason = typeof raw.error === 'string' ? raw.error : ''

  return {
    ...raw,
    index,
    beadId,
    artifactBeadId,
    title: summarizeJobTitle(fullTitle, `#${index}`, 42),
    fullTitle,
    prompt: fullTitle,
    role: raw.type ?? raw.role ?? '',
    beadType: raw.type ?? raw.beadType ?? '',
    status,
    statusLabel: formatJobStatus(status),
    tone: beadTone(status),
    dependsOn,
    blockedBy,
    failureReason,
    updatedAt: raw.updatedAt ?? raw.completedAt ?? raw.startedAt ?? raw.createdAt ?? job.value.updatedAt,
    lastTelemetryAt: normalizeTimestamp(
      raw.lastTelemetryAt ??
      raw.last_telemetry_at ??
      raw.latestTelemetryAt ??
      raw.latest_telemetry_at ??
      raw.telemetryUpdatedAt ??
      raw.telemetry_updated_at,
    ),
    usage: raw.usage ?? null,
    maxCostUSD: typeof raw.maxCostUSD === 'number' ? raw.maxCostUSD : null,
    outputInline: raw.output ?? null,
    runId,
    isLive: true,
    canKill: ['queued', 'in_progress'].includes(status),
    canRetry: status === 'failed' && !failureReason.startsWith('Dependency'),
    canSkip: status === 'failed' && !failureReason.startsWith('Dependency'),
  }
}

function normalizeTimestamp(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const date = new Date(value)
    return Number.isFinite(date.getTime()) ? date.toISOString() : ''
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    if (!Number.isFinite(parsed)) return ''
    const date = new Date(parsed)
    return Number.isFinite(date.getTime()) ? date.toISOString() : ''
  }
  return ''
}

function formatTokens(usage) {
  if (!usage) return ''
  const parts = []
  if (usage.inputTokens) parts.push(`${Number(usage.inputTokens).toLocaleString()} in`)
  if (usage.outputTokens) parts.push(`${Number(usage.outputTokens).toLocaleString()} out`)
  return parts.join(' / ')
}

function formatDuration(value) {
  if (!value) return ''
  const ms = Number(value)
  if (!Number.isFinite(ms) || ms <= 0) return ''
  if (ms < 1000) return `${ms}ms`
  const seconds = Math.round(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
}

function formatCurrency(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return ''
  return `$${value.toFixed(2)}`
}

function detectStalledBead(bead) {
  if (bead.status !== 'in_progress') return false
  // If we have usage data (tokens, turns), the bead is actively running
  if (bead.usage) return false
  // If the bead has recent telemetry, it's not stalled
  if (bead.lastTelemetryAt) {
    const telemetryAgeMs = Date.now() - new Date(bead.lastTelemetryAt).getTime()
    if (Number.isFinite(telemetryAgeMs) && telemetryAgeMs < 30 * 60 * 1000) return false
  }
  if (!bead.updatedAt) return false
  // Only flag as stalled after 30 minutes with no updates
  const ageMs = Date.now() - new Date(bead.updatedAt).getTime()
  return Number.isFinite(ageMs) && ageMs > 30 * 60 * 1000
}

function formatDetail(value) {
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2)
  return looksLikeCode(text) ? `\`\`\`\n${text}\n\`\`\`` : text
}

function looksLikeCode(text) {
  if (!text || typeof text !== 'string') return false
  const trimmed = text.trim()
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) return true
  return trimmed.includes('\n') && !/^#{1,6}\s/m.test(trimmed)
}

function normalizeConversationContent(content) {
  if (typeof content === 'string') return content
  if (Array.isArray(content)) {
    return content.map((block) => {
      if (typeof block === 'string') return block
      if (block?.text) return block.text
      if (block?.toolUse) return `[Tool ${block.toolUse.name}] ${JSON.stringify(block.toolUse.input, null, 2)}`
      if (block?.toolResult) return `[Result] ${JSON.stringify(block.toolResult, null, 2)}`
      if (block?.type === 'tool_use') return `[Tool ${block.name}] ${JSON.stringify(block.input, null, 2)}`
      if (block?.type === 'tool_result') return `[Result] ${JSON.stringify(block.content, null, 2)}`
      return JSON.stringify(block, null, 2)
    }).join('\n')
  }
  return JSON.stringify(content, null, 2)
}

function formatConversationMessage(message) {
  if (!message || typeof message !== 'object') return ''

  const parts = []
  const content = normalizeConversationContent(message.content)
  if (content) parts.push(content)

  if (message.toolUse && typeof message.toolUse === 'object') {
    const toolName = message.toolUse.toolName || message.toolUse.name || 'tool'
    const args = message.toolUse.argsJson ?? message.toolUse.input ?? ''
    parts.push(`[Tool ${toolName}] ${typeof args === 'string' ? args : JSON.stringify(args, null, 2)}`)
  }

  if (message.toolResult && typeof message.toolResult === 'object') {
    const toolName = message.toolResult.toolName || message.toolResult.name || 'tool'
    const result = message.toolResult.resultJson ?? message.toolResult.content ?? ''
    parts.push(`[Result ${toolName}] ${typeof result === 'string' ? result : JSON.stringify(result, null, 2)}`)
  }

  return parts.filter(Boolean).join('\n\n')
}

function normalizeConversation(data) {
  if (!data || typeof data !== 'object') return null
  return {
    systemPrompt: typeof data.systemPrompt === 'string' ? data.systemPrompt : '',
    messages: Array.isArray(data.messages)
      ? data.messages.map((message) => ({
          role: typeof message?.role === 'string' ? message.role : 'unknown',
          content: formatConversationMessage(message),
        }))
      : [],
  }
}

// #763: GET .../conversation returns a discriminated union (ADR-035 artifact
// transport) rather than a raw { systemPrompt, messages } payload —
// `kind: 'inline'` embeds the conversation directly under `conversation`;
// `kind: 'download'` means the transcript exceeded the inline byte cap and
// the caller must follow the short-TTL presigned `downloadUrl` to fetch the
// full transcript from S3. That URL is absolute and pre-signed, so it is
// fetched directly (not through the orchestrator request client, which
// would prepend the orchestrator base URL and inject API auth headers that
// don't belong on an S3 request).
//
// The shared authority is `describeConversationReadResponseDefects` in
// `shared/src/conversation-artifact-contract.ts`; 86proof-site cannot import
// `@beads/shared` into its webpack build (no TS loader — see the note in
// useMarkdown.js), so this mirrors that contract's shape checks and names the
// offending field. A malformed response therefore surfaces a NAMED error
// through `conversationErrors` (rendered in the Transcript panel) instead of
// degrading to a silent blank panel. Keep in sync with the shared validator if
// the contract changes.
const CONVERSATION_SHA256_HEX_RE = /^[0-9a-f]{64}$/
// Mirror the byte caps in shared/src/conversation-artifact-contract.ts. The
// shared validators bound byteLength above as well as below, per branch.
const CONVERSATION_ARTIFACT_BYTE_CAP = 64 * 1024 * 1024 // download hard cap
const CONVERSATION_INLINE_BYTE_CAP = 240 * 1024 // inline cap

function isNonEmptyString(value) {
  return typeof value === 'string' && value.length > 0
}
function isPositiveIntegerWithinCap(value, cap) {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 && value <= cap
}
function isSha256Hex(value) {
  return typeof value === 'string' && CONVERSATION_SHA256_HEX_RE.test(value)
}
function isIsoTimestamp(value) {
  return typeof value === 'string' && value.length > 0 && Number.isFinite(Date.parse(value))
}
function isNonNegativeNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

// Validate the REQUIRED scalar fields of a conversation message (role in
// {user,assistant,system}, string content) — the fields this component renders.
// This does NOT deep-validate the OPTIONAL `toolUse`/`toolResult` payloads that
// shared normalizeConversationMessage (conversation-types.ts) also checks: that
// deep validation is intentionally DELEGATED to the server-side shared
// normalization, which produces every conversation body the console receives
// (inline-from-server and download-from-S3 are both shared-normalized at the
// source), so a malformed tool payload is unreachable here; and
// formatConversationMessage renders tool payloads defensively (never blank).
function describeConversationMessageDefect(message, index) {
  if (!message || typeof message !== 'object' || Array.isArray(message)) {
    return `messages[${index}]: expected a message object`
  }
  if (message.role !== 'user' && message.role !== 'assistant' && message.role !== 'system') {
    return `messages[${index}].role: expected "user" | "assistant" | "system"`
  }
  if (typeof message.content !== 'string') {
    return `messages[${index}].content: expected a string`
  }
  return ''
}

// Validate the REQUIRED BeadConversation fields (mirroring the required-field
// set of shared normalizeBeadConversation in shared/src/conversation-types.ts):
// systemPrompt (string), messages (array of valid messages — see
// describeConversationMessageDefect), tokenUsage (array of
// { inputTokens >= 0, outputTokens >= 0 }), totalDurationMs (>= 0), status
// ('completed'|'failed'), and partial (boolean). This is NOT a byte-for-byte
// mirror of normalizeBeadConversation: the shared function ALSO deep-validates
// optional per-message tool payloads, which are DELEGATED to the server-side
// shared normalization (see describeConversationMessageDefect) — every body the
// console receives is shared-normalized at the source, so those are unreachable
// here, and the frontend validates the required scalar fields plus everything it
// renders. Validating ONLY "messages is an array" let a wrong-shape body ({} or
// {messages:[]}-missing-fields) coerce to a blank panel — the exact #763 symptom.
// A repo-level drift guard (tests/shared-mirror-drift.test.js) asserts the fields
// validated here stay in sync with shared. `label` distinguishes the inline vs
// downloaded-body call sites. NOTE: a VALID BeadConversation with an empty
// messages array is legitimately empty (not malformed) — it passes here and the
// panel renders an explicit "No messages recorded" empty state, never a blank.
function describeBeadConversationDefect(conversation, label) {
  if (!conversation || typeof conversation !== 'object' || Array.isArray(conversation)) {
    return `${label}: expected a BeadConversation object`
  }
  if (typeof conversation.systemPrompt !== 'string') {
    return `${label}.systemPrompt: expected a string`
  }
  if (!Array.isArray(conversation.messages)) {
    return `${label}.messages: expected an array`
  }
  if (!Array.isArray(conversation.tokenUsage)) {
    return `${label}.tokenUsage: expected an array`
  }
  if (conversation.status !== 'completed' && conversation.status !== 'failed') {
    return `${label}.status: expected "completed" | "failed"`
  }
  if (!isNonNegativeNumber(conversation.totalDurationMs)) {
    return `${label}.totalDurationMs: expected a non-negative number`
  }
  if (typeof conversation.partial !== 'boolean') {
    return `${label}.partial: expected a boolean`
  }
  for (let i = 0; i < conversation.messages.length; i += 1) {
    const messageDefect = describeConversationMessageDefect(conversation.messages[i], i)
    if (messageDefect) return `${label}.${messageDefect}`
  }
  for (let i = 0; i < conversation.tokenUsage.length; i += 1) {
    const entry = conversation.tokenUsage[i]
    if (!entry || typeof entry !== 'object' || !isNonNegativeNumber(entry.inputTokens) || !isNonNegativeNumber(entry.outputTokens)) {
      return `${label}.tokenUsage[${i}]: expected { inputTokens >= 0, outputTokens >= 0 }`
    }
  }
  return ''
}

function describeConversationReadDefect(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return 'response is not an object with kind "inline" | "download"'
  }
  if (data.kind === 'inline') {
    // Mirror the shared inline branch: full BeadConversation + byteLength (<=
    // inline cap) + sha256.
    const convDefect = describeBeadConversationDefect(data.conversation, 'conversation')
    if (convDefect) return convDefect
    if (!isPositiveIntegerWithinCap(data.byteLength, CONVERSATION_INLINE_BYTE_CAP)) {
      return `byteLength: kind "inline" response must carry a positive integer byte count <= ${CONVERSATION_INLINE_BYTE_CAP} (CONVERSATION_INLINE_BYTE_CAP)`
    }
    if (!isSha256Hex(data.sha256)) {
      return 'sha256: kind "inline" response must carry a 64-char lowercase hex sha256'
    }
    return ''
  }
  if (data.kind === 'download') {
    // Mirror ALL of the shared contract's download-wrapper checks, not just
    // downloadUrl: byteLength (<= artifact cap), sha256, and expiresAt.
    if (!isNonEmptyString(data.downloadUrl)) {
      return 'downloadUrl: kind "download" response must carry a non-empty downloadUrl string'
    }
    if (!isPositiveIntegerWithinCap(data.byteLength, CONVERSATION_ARTIFACT_BYTE_CAP)) {
      return `byteLength: kind "download" response must carry a positive integer byte count <= ${CONVERSATION_ARTIFACT_BYTE_CAP} (CONVERSATION_ARTIFACT_BYTE_CAP)`
    }
    if (!isSha256Hex(data.sha256)) {
      return 'sha256: kind "download" response must carry a 64-char lowercase hex sha256'
    }
    if (!isIsoTimestamp(data.expiresAt)) {
      return 'expiresAt: kind "download" response must carry an ISO 8601 timestamp'
    }
    return ''
  }
  return `kind: unrecognized conversation response kind ${JSON.stringify(data.kind)} (expected "inline" or "download")`
}

async function resolveConversationPayload(data) {
  const defect = describeConversationReadDefect(data)
  if (defect) {
    throw new Error(`Malformed transcript response (${defect})`)
  }
  if (data.kind === 'inline') {
    return data.conversation
  }
  // kind === 'download' — the wrapper is validated above. Fetch the presigned
  // S3 URL directly. A network/CORS failure REJECTS the fetch (vs an HTTP
  // error, which resolves with ok=false); distinguish the two so the operator
  // can tell a CORS/reachability failure (the exact class this PR's S3 CORS
  // config targets) from an expired/denied presigned URL.
  let downloadResponse
  try {
    downloadResponse = await fetch(data.downloadUrl)
  } catch (networkError) {
    throw new Error(`Could not reach transcript storage (network or CORS error): ${networkError?.message ?? networkError}`)
  }
  if (!downloadResponse.ok) {
    throw new Error(`Failed to download transcript (HTTP ${downloadResponse.status})`)
  }
  // #763 (round-2 H1): a 200-OK-but-malformed body (`{}`, wrong schema, or
  // TRUNCATED/unparseable bytes) would otherwise coerce to `{ messages: [] }`
  // in normalizeConversation and render a BLANK panel — the exact #763 symptom,
  // on the large-transcript path this PR targets. A truncated body makes
  // `.json()` throw a raw SyntaxError, so parse under try/catch and surface the
  // named malformed-download error (not an opaque SyntaxError); then validate
  // the parsed body through the SAME shape check the inline branch uses.
  let body
  try {
    body = await downloadResponse.json()
  } catch (parseError) {
    throw new Error(`Malformed transcript download (body is not valid JSON: ${parseError?.message ?? parseError})`)
  }
  const bodyDefect = describeBeadConversationDefect(body, 'downloaded transcript')
  if (bodyDefect) {
    throw new Error(`Malformed transcript download (${bodyDefect})`)
  }
  return body
}

function formatDiagnosticLabel(item) {
  if (!item || typeof item !== 'object') return 'Unknown diagnostic'
  return item.label || item.summary || item.query || item.topic || formatJobStatus(item.kind || item.gap_type || 'unknown')
}

function formatEvidenceLabel(item) {
  if (!item || typeof item !== 'object') return 'Evidence'
  const parts = [item.query || item.title || item.standard_id || 'Evidence']
  if (item.source) parts.push(`(${formatJobStatus(item.source)})`)
  return parts.join(' ')
}

function syncAnswersFromJob() {
  if (!questionRows.value.length) {
    answers.value = []
    return
  }
  // Don't overwrite answers the user is actively typing
  const userHasInput = answers.value.some((a) => typeof a === 'string' && a.trim().length > 0)
  if (userHasInput) return

  const existing = normalizeAnswerMap(job.value.answers)
  answers.value = questionRows.value.map((row) => existing.get(String(row.index)) || '')
}

function isPollableJobStatus(status) {
  return POLL_STATUSES.has(status)
}

function stopJobPolling() {
  if (pollTimer !== null) {
    clearTimeout(pollTimer)
    pollTimer = null
  }
  pollErrorCount = 0
}

function scheduleJobPoll() {
  if (pollTimer !== null || !isPollableJobStatus(job.value.status)) return

  pollTimer = setTimeout(async () => {
    pollTimer = null
    await pollJobStatus()
  }, POLL_INTERVAL_MS)
}

function syncJobPolling() {
  if (isPollableJobStatus(job.value.status)) {
    scheduleJobPoll()
  } else {
    stopJobPolling()
  }
}

async function fetchJob() {
  const data = await requestJson(`/jobs/${jobId}`)
  job.value = normalizeJob(data)
  syncAnswersFromJob()
}

async function fetchBeads({ background = false } = {}) {
  if (!workspaceId.value) return
  if (!background) {
    beadsLoading.value = true
    beadsError.value = ''
  }
  try {
    const data = await requestJson(`/jobs/${workspaceId.value}/${jobId}/beads`)
    const beadList = Array.isArray(data) ? data : (data.beads ?? [])
    beads.value = beadList.map(normalizeBeadRecord)
    beadsError.value = ''
  } catch (fetchError) {
    if (!background) {
      beads.value = []
      beadsError.value = 'Step state is not available for this run.'
      logger.warn('Failed to fetch run steps', fetchError)
    }
  } finally {
    if (!background) beadsLoading.value = false
  }
}

async function fetchResults({ background = false } = {}) {
  if (!workspaceId.value) return
  try {
    const data = await requestJson(`/jobs/${workspaceId.value}/${jobId}/results`)
    results.value = data.results ?? null
  } catch (fetchError) {
    if (!background) {
      results.value = null
      logger.warn('Failed to fetch run results', fetchError)
    }
  }
}

async function loadData({ showLoading = false } = {}) {
  if (showLoading) loading.value = true
  stopJobPolling()
  error.value = ''
  actionMessage.value = ''
  beadsError.value = ''
  try {
    await fetchJob()
    syncJobPolling()
    loading.value = false
    // #508: during PLANNING the per-bead endpoints (beads/results/progress) do not
    // exist yet and 404 on every poll; the planner stage comes from plannerPhase.
    const initialFetches = isPlanning.value ? [] : [fetchBeads(), fetchResults()]
    void Promise.allSettled(initialFetches)
  } catch (fetchError) {
    error.value = 'Failed to load run.'
    logger.warn('Failed to fetch run detail', fetchError)
    loading.value = false
  }
}

async function pollJobStatus() {
  pollBusy.value = true
  try {
    await fetchJob()
    pollErrorCount = 0
    // #508: skip the per-bead endpoints during PLANNING (they 404 until beads exist).
    const backgroundFetches = isPlanning.value
      ? []
      : [fetchBeads({ background: true }), fetchResults({ background: true })]
    await Promise.allSettled(backgroundFetches)
    syncJobPolling()
  } catch (pollError) {
    pollErrorCount += 1
    logger.warn('Failed to poll run detail', pollError)

    if (pollErrorCount < MAX_POLL_ERRORS) {
      scheduleJobPoll()
    }
  } finally {
    pollBusy.value = false
  }
}

function toggleBead(index) {
  beadActionError.value = ''
  selectedBeadIndex.value = selectedBeadIndex.value === index ? null : index
  if (selectedBeadIndex.value !== null) {
    const bead = executionEntries.value.find((entry) => entry.index === selectedBeadIndex.value)
    if (bead?.isLive) void fetchBeadOutput(bead)
  }
}

async function fetchBeadOutput(bead) {
  if (!workspaceId.value || !bead?.isLive) return
  const key = bead.beadId
  if (beadOutputLoading.value[key]) return
  beadOutputLoading.value = { ...beadOutputLoading.value, [key]: true }
  beadOutputErrors.value = { ...beadOutputErrors.value, [key]: '' }
  try {
    const response = await request(`/jobs/${workspaceId.value}/${jobId}/beads/${bead.index}/output`)
    if (!response.ok) {
      if (response.status === 404) {
        beadOutputs.value = { ...beadOutputs.value, [key]: { _noOutput: true } }
        return
      }
      throw new Error(response.data?.error || response.data?.message || `HTTP ${response.status}`)
    }
    beadOutputs.value = { ...beadOutputs.value, [key]: response.data.output }
  } catch (fetchError) {
    beadOutputErrors.value = { ...beadOutputErrors.value, [key]: fetchError.message }
  } finally {
    beadOutputLoading.value = { ...beadOutputLoading.value, [key]: false }
  }
}

async function onConversationToggle(bead, event) {
  if (!event.target.open || !workspaceId.value || bead?.runId == null) return
  const key = bead.beadId
  if (conversations.value[key] || conversationLoading.value[key]) return
  conversationLoading.value = { ...conversationLoading.value, [key]: true }
  conversationErrors.value = { ...conversationErrors.value, [key]: '' }
  conversationErrorKinds.value = { ...conversationErrorKinds.value, [key]: '' }
  try {
    const response = await request(`/beads/${workspaceId.value}/${jobId}/${bead.artifactBeadId}/runs/${bead.runId}/conversation`)
    if (!response.ok) {
      const message = response.data?.error || response.data?.message || `HTTP ${response.status}`
      conversationErrors.value = { ...conversationErrors.value, [key]: message }
      // ONLY a 404 (the transcript artifact is not written yet — expected for an
      // in-progress bead) is the "not ready" case the in-progress mask should
      // hide. A 5xx/403/etc. is a real failure and must be surfaced even while
      // the bead is running, not masked as "will be available".
      const kind = response.status === 404 ? 'unavailable' : 'load-failed'
      conversationErrorKinds.value = { ...conversationErrorKinds.value, [key]: kind }
      return
    }
    const payload = await resolveConversationPayload(response.data)
    conversations.value = { ...conversations.value, [key]: normalizeConversation(payload) }
  } catch (fetchError) {
    // A real load failure (malformed response / CORS / download / parse). This
    // must be surfaced even while the bead is in progress — masking it defeats
    // the visibility the #763 fix adds.
    conversationErrors.value = { ...conversationErrors.value, [key]: fetchError.message }
    conversationErrorKinds.value = { ...conversationErrorKinds.value, [key]: 'load-failed' }
  } finally {
    conversationLoading.value = { ...conversationLoading.value, [key]: false }
  }
}

function beadCompositeId(bead) {
  return `${jobId}-${bead.beadId}`
}

function startRetry(bead) {
  retryTargetIndex.value = bead.index
  retryBudget.value = suggestBudget(bead)
  beadActionError.value = ''
}

function cancelRetry() {
  retryTargetIndex.value = null
  retryBudget.value = ''
  beadActionError.value = ''
}

function suggestBudget(bead) {
  const estimated = typeof bead.usage?.estimatedCostUSD === 'number' ? bead.usage.estimatedCostUSD : 0
  const current = typeof bead.maxCostUSD === 'number' ? bead.maxCostUSD : 2
  return Math.max(estimated * 1.5, current * 1.5, 0.5).toFixed(1)
}

async function refreshAfterBeadAction() {
  results.value = null
  await Promise.allSettled([fetchJob(), fetchBeads(), fetchResults()])
  syncJobPolling()
}

async function retryBead(bead) {
  retryBusyIndex.value = bead.index
  beadActionError.value = ''
  try {
    const body = {}
    const budget = Number(retryBudget.value)
    if (Number.isFinite(budget) && budget > 0) body.maxCostUSD = budget
    await requestJson(`/beads/${beadCompositeId(bead)}/run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    cancelRetry()
    await refreshAfterBeadAction()
  } catch (actionError) {
    beadActionError.value = actionError.message || 'Failed to retry step.'
    logger.warn('Failed to retry step', actionError)
  } finally {
    retryBusyIndex.value = null
  }
}

async function killBead(bead) {
  killBusyIndex.value = bead.index
  beadActionError.value = ''
  try {
    await requestJson(`/beads/${beadCompositeId(bead)}/kill`, { method: 'POST' })
    await refreshAfterBeadAction()
  } catch (actionError) {
    beadActionError.value = actionError.message || 'Failed to kill step.'
    logger.warn('Failed to kill step', actionError)
  } finally {
    killBusyIndex.value = null
  }
}

async function skipBead(bead) {
  skipBusyIndex.value = bead.index
  beadActionError.value = ''
  try {
    await requestJson(`/beads/${beadCompositeId(bead)}/skip`, { method: 'POST' })
    await refreshAfterBeadAction()
  } catch (actionError) {
    beadActionError.value = actionError.message || 'Failed to skip step.'
    logger.warn('Failed to skip step', actionError)
  } finally {
    skipBusyIndex.value = null
  }
}

async function submitAnswers() {
  if (!workspaceId.value) return
  actionBusy.value = true
  actionMessage.value = ''
  try {
    const payload = Object.fromEntries(questionRows.value.map((row) => [String(row.index), answers.value[row.index].trim()]))
    await requestJson(`/jobs/${workspaceId.value}/${jobId}/answers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answers: payload }),
    })
    actionMessage.value = 'Answers submitted.'
    await loadData()
  } catch (submitError) {
    actionMessage.value = `Failed: ${submitError.message}`
    logger.warn('Failed to submit answers', submitError)
  } finally {
    actionBusy.value = false
  }
}

// #643: manual resume from a WAITING_CREDENTIALS pause whose reason
// (claude_auth, claude_quota) is not fixed by a workspace credential PATCH
// (the git_* auto-resume flow filters those out — see WorkerService
// resumeWaitingCredentialsJobsForWorkspace). Calls the dedicated
// POST /jobs/{ws}/{job}/resume endpoint (NOT a flat field-mutating PATCH):
// the server enforces the WAITING_CREDENTIALS + claude_* reason precondition
// (no DONE/CANCELLED/EXECUTING resurrection), applies the same
// optimistic-concurrency guard as the auto-resume path, AND drives the
// cross-runtime dispatch — the SQS wakeup for bedrock, including the PLANNING
// planner re-dispatch — so a resumed bedrock run actually progresses instead
// of hanging (bedrock has no /worker/poll). The button is disabled while a
// resume phase is present but not yet loaded; when the reason is a claude_*
// pause but resumePhase is absent the template renders an explanatory
// fallback + workspace link instead of an inert disabled button.
async function resumeRun() {
  if (!workspaceId.value || !resumePhase.value) return
  actionBusy.value = true
  actionMessage.value = ''
  try {
    const result = await requestJson(`/jobs/${workspaceId.value}/${jobId}/resume`, {
      method: 'POST',
    })
    const resumedPhase = result && typeof result === 'object' ? result.phase : undefined
    actionMessage.value = resumedPhase
      ? `Resume requested — run returning to ${resumedPhase}.`
      : 'Resume requested.'
    await loadData()
  } catch (resumeError) {
    actionMessage.value = `Failed: ${resumeError.message}`
    logger.warn('Failed to resume run', resumeError)
  } finally {
    actionBusy.value = false
  }
}

async function approvePlan() {
  if (!workspaceId.value) return
  actionBusy.value = true
  actionMessage.value = ''
  stalenessInfo.value = null
  try {
    const response = await request(`/jobs/${workspaceId.value}/${jobId}/approve`, { method: 'POST' })
    const result = await appWorkspaceTaskClient.resolveTaskResponse({ request, workspaceId: workspaceId.value, response })
    // #557: a stale workspace baseline means the approval was accepted but execution
    // did NOT activate. Surface the decision instead of falsely reporting success.
    if (result && result.stale === true && result.staleness) {
      stalenessInfo.value = result.staleness
      actionMessage.value = 'Workspace is behind the target branch — choose how to proceed.'
      return
    }
    actionMessage.value = 'Plan approved.'
    await loadData()
  } catch (approveError) {
    actionMessage.value = `Failed: ${approveError.message}`
    logger.warn('Failed to approve run plan', approveError)
  } finally {
    actionBusy.value = false
  }
}

// #557: resolve a stale-workspace approval via POST /workspaces/{id}/staleness/decide.
// 'continue' returns the activation result synchronously; 'sync'/'new-workspace'
// return a task that resolveTaskResponse polls to completion.
async function decideStaleness(decision) {
  // Re-entry guard: a fast double-click must not submit duplicate decisions
  // (e.g. duplicate workspace creation).
  if (!workspaceId.value || stalenessBusy.value) return
  stalenessBusy.value = true
  actionMessage.value = ''
  try {
    const response = await request(`/workspaces/${workspaceId.value}/staleness/decide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobId, decision }),
    })
    const result = await appWorkspaceTaskClient.resolveTaskResponse({ request, workspaceId: workspaceId.value, response })
    stalenessInfo.value = null
    // new-workspace creates a fresh workspace (no run yet) — route to it.
    // The server returns newWorkspaceId (not a job id).
    if (decision === 'new-workspace' && result && result.newWorkspaceId) {
      router.push({ name: 'ConsoleWorkspaceDetail', params: { id: result.newWorkspaceId } })
      return
    }
    await loadData()
    // Set after loadData so the confirmation is not immediately cleared by the reload.
    actionMessage.value = decision === 'continue'
      ? 'Plan approved against the current workspace baseline.'
      : 'Workspace updated — execution is starting.'
  } catch (decideError) {
    // Leave stalenessInfo set so the operator can retry from the same panel.
    actionMessage.value = `Failed: ${decideError.message}`
    logger.warn('Failed to submit staleness decision', decideError)
  } finally {
    stalenessBusy.value = false
  }
}

// #557: on entry into pending_approval, issue an authoritative staleness
// check via the async task path (GET /workspaces/{id}/staleness returns 202
// with a taskId, then resolveTaskResponse polls to { stale, staleness }) so
// the decision panel renders on first load and after a reload without
// requiring a second Approve Plan click. Mirrors the helper usage in
// approvePlan() and decideStaleness() — the task path is the only allowed
// route (no ?mode=refresh) because that path offloads git fetch / remote-head
// work from the API Lambda.
async function performUpfrontStalenessCheck() {
  if (!workspaceId.value) return
  // Claim a generation; a later check (a new pending_approval entry) supersedes
  // this one, so a stale result resolving after leave/re-entry is ignored.
  const generation = (stalenessCheckGeneration.value += 1)
  try {
    const response = await request(`/workspaces/${workspaceId.value}/staleness`)
    const result = await appWorkspaceTaskClient.resolveTaskResponse({ request, workspaceId: workspaceId.value, response })
    // Drop the result if a newer check superseded this one, the run advanced out
    // of pending_approval, or the operator dismissed the panel while the task was
    // resolving — the check is best-effort and must not override newer state or
    // an explicit operator action.
    if (stalenessCheckGeneration.value !== generation || job.value.status !== 'pending_approval' || stalenessDismissed.value) return
    // Authoritative for the current entry: show when stale, clear when current —
    // so a current-generation not-stale result supersedes any stale panel an
    // earlier raced check may have shown, rather than only ever setting it.
    stalenessInfo.value = result && result.stale === true && result.staleness
      ? result.staleness
      : null
  } catch (checkError) {
    logger.warn('Failed to perform upfront staleness check', checkError)
    // Ignore a superseded check's failure (or one whose run already advanced) so
    // it cannot reset a newer entry's state or show a stale breadcrumb.
    if (stalenessCheckGeneration.value !== generation || job.value.status !== 'pending_approval') return
    // Best-effort recovery: reset the marker so a re-entry retries, and surface a
    // non-fatal breadcrumb. Approve Plan remains the authoritative path that
    // re-runs the same staleness logic and surfaces the panel (the #557 fallback).
    stalenessCheckInitiated.value = false
    if (!stalenessInfo.value) {
      actionMessage.value = 'Could not check workspace freshness automatically — click Approve Plan to continue.'
    }
  }
}

function dismissStaleness() {
  stalenessInfo.value = null
  // #574: remember the dismissal so an in-flight upfront check cannot revive it.
  stalenessDismissed.value = true
  actionMessage.value = ''
}

async function deleteJob() {
  if (!workspaceId.value) return
  actionBusy.value = true
  actionMessage.value = ''
  try {
    await requestJson(`/jobs/${workspaceId.value}/${jobId}`, { method: 'DELETE' })
    router.push({ name: 'ConsoleJobs' })
  } catch (deleteError) {
    actionMessage.value = `Delete failed: ${deleteError.message}`
    logger.warn('Failed to delete run', deleteError)
  } finally {
    actionBusy.value = false
  }
}

// #557: on transition into pending_approval (first load or live arrival), kick
// off the upfront staleness check exactly once so the decision panel surfaces
// up front. Resetting the marker when the run leaves pending_approval lets a
// future re-entry trigger a fresh check, while polling ticks that keep the
// status unchanged do not re-fire the watcher (and so do not respawn tasks).
watch(() => job.value.status, (status) => {
  if (status === 'pending_approval') {
    if (!stalenessCheckInitiated.value) {
      stalenessCheckInitiated.value = true
      void performUpfrontStalenessCheck()
    }
  } else {
    // Run left pending_approval: reset the one-shot markers so a future
    // re-entry runs a fresh upfront check.
    stalenessCheckInitiated.value = false
    stalenessDismissed.value = false
  }
})

// #557: drop the staleness panel if the run advances out of pending_approval
// (resolved by another actor, or once a decision activates execution).
watch(() => job.value.status, (status) => {
  if (status && status !== 'pending_approval') stalenessInfo.value = null
})

onMounted(() => {
  loadData({ showLoading: true })
})

onUnmounted(() => {
  stopJobPolling()
})
</script>

<style scoped>
.planner-progress {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.planner-progress__bar {
  height: 4px;
  background: var(--shell-border);
  border-radius: 2px;
  overflow: hidden;
}

.planner-progress__fill {
  height: 100%;
  background: var(--status-active);
  border-radius: 2px;
  transition: width 0.4s ease;
}

.job-detail__stat {
  margin-left: 12px;
  color: var(--shell-text-muted);
}

.job-detail__prose {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.65;
  color: var(--shell-text);
}

.job-detail__prose--boxed {
  margin-top: 10px;
  padding: 12px 16px;
  background: var(--shell-surface);
  border: 1px solid var(--shell-border);
  max-height: 400px;
  overflow: auto;
}

.job-detail__prose--nested {
  padding: 12px;
}

.job-detail__action-block {
  padding: 20px 24px;
  margin-bottom: 24px;
  border-radius: 8px;
  border-left: 4px solid #f59e0b;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(245, 158, 11, 0.04) 100%);
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.1);
}

.job-detail__action-block p {
  margin: 0 0 16px;
  font-size: 0.95rem;
  color: var(--shell-text);
}

.job-detail__action-block strong {
  color: #b45309;
}

.job-detail__question {
  margin-bottom: 12px;
}

.job-detail__question-label,
.job-detail__diag-label,
.job-detail__subheading {
  font-family: var(--shell-mono-font);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--shell-text-muted);
}

.job-detail__question-label {
  display: block;
  margin-bottom: 4px;
}

.job-detail__plan-list {
  margin: 0;
  padding: 0;
}

.job-detail__plan-entry {
  border: 1px solid var(--shell-border);
  border-radius: 3px;
  margin-bottom: 8px;
  background: var(--shell-surface-strong);
}

.job-detail__plan-entry:last-child {
  margin-bottom: 0;
}

.job-detail__plan-entry[open] {
  background: var(--shell-surface);
}

.job-detail__plan-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  font-size: 0.82rem;
  color: var(--shell-text);
  cursor: pointer;
  list-style: none;
}

.job-detail__plan-summary::-webkit-details-marker {
  display: none;
}

.job-detail__plan-summary::before {
  content: "\25B6";
  font-size: 0.55rem;
  color: var(--shell-text-muted);
  transition: transform 0.15s;
}

.job-detail__plan-entry[open] > .job-detail__plan-summary::before {
  transform: rotate(90deg);
}

.job-detail__plan-index {
  min-width: 32px;
  font-weight: 700;
  color: var(--shell-accent);
}

.job-detail__plan-title {
  flex: 1;
}

.job-detail__plan-deps,
.job-detail__status-line,
.job-detail__message {
  color: var(--shell-text-muted);
}

.job-detail__status-line,
.job-detail__message,
.job-detail__conversation-role {
  font-size: 0.84rem;
}

.job-detail__status-line--error {
  color: var(--status-failed);
}

.dag {
  --dag-node-bg: var(--shell-surface-strong);
  --dag-node-border: var(--shell-border);
  --dag-connector: rgba(139, 78, 45, 0.2);
  position: relative;
}

.dag__phase {
  position: relative;
}

.dag__phase:not(:last-child)::after {
  content: "";
  position: absolute;
  left: 28px;
  bottom: -12px;
  width: 2px;
  height: 24px;
  background: linear-gradient(to bottom, var(--dag-connector), transparent);
}

.dag__phase-row {
  display: flex;
  gap: 16px;
  padding: 12px 0;
}

.dag__phase-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 56px;
  padding-top: 10px;
}

.dag__phase-label::before {
  content: "";
  width: 10px;
  height: 10px;
  margin-bottom: 6px;
  border: 2px solid var(--shell-accent);
  border-radius: 50%;
  background: var(--shell-surface);
}

.dag__phase-label span {
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--shell-text-muted);
}

.dag__nodes {
  display: grid;
  gap: 10px;
  flex: 1;
}

.dag__node {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: none;
  border-radius: 6px;
  background: var(--dag-node-bg);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), inset 0 0 0 1px var(--dag-node-border);
  font-size: 0.82rem;
  text-align: left;
  cursor: pointer;
}

.dag__node::before {
  content: "";
  position: absolute;
  left: 0;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 0 2px 2px 0;
  background: var(--shell-text-muted);
}

.dag__node--ready::before { background: #22c55e; }
.dag__node--active::before { background: #3b82f6; }
.dag__node--pending::before { background: #f59e0b; }
.dag__node--failed::before { background: #ef4444; }
.dag__node--neutral::before { background: #9ca3af; }

.dag__node--selected {
  box-shadow: 0 2px 10px rgba(139, 78, 45, 0.18), inset 0 0 0 2px var(--shell-accent);
}

.dag__node-index {
  min-width: 24px;
  font-size: 0.7rem;
  font-weight: 800;
  color: var(--shell-accent);
}

.dag__node-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--shell-text);
}

.dag__node-usage {
  font-size: 0.68rem;
  color: var(--shell-text-muted);
}

.dag__node-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dag__node-dot--ready { background: #22c55e; }
.dag__node-dot--active { background: #3b82f6; }
.dag__node-dot--pending { background: #f59e0b; }
.dag__node-dot--failed { background: #ef4444; }
.dag__node-dot--neutral { background: #9ca3af; }

.dag__detail {
  margin: 4px 0 16px 72px;
  padding: 16px 20px;
  border-radius: 6px;
  background: var(--shell-surface);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), inset 0 0 0 1px var(--shell-border);
}

.dag__detail-header,
.dag__detail-title-row,
.job-detail__actions-row,
.job-detail__subsection-header,
.job-detail__meta-list div {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.dag__detail-index {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--shell-accent);
}

.dag__detail-title {
  margin: 8px 0 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--shell-text);
}

.job-detail__close-button {
  min-width: 0;
}

.job-detail__diag-grid,
.job-detail__diagnostics-grid {
  display: grid;
  gap: 12px;
  margin-top: 16px;
}

.job-detail__diag-grid {
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}

.job-detail__diag-item,
.job-detail__diagnostic-block {
  padding: 12px 14px;
  border: 1px solid var(--shell-border);
  background: var(--shell-surface-strong);
}

.job-detail__diag-item span:last-child {
  display: block;
  margin-top: 8px;
  font-size: 0.88rem;
  color: var(--shell-text);
}

.job-detail__failure-block,
.job-detail__stalled-note,
.job-detail__subsection,
.job-detail__acceptance,
.job-detail__retry-form {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--shell-border);
}

.job-detail__failure-text,
.job-detail__conversation-pre {
  margin: 8px 0 0;
  padding: 12px;
  background: var(--shell-surface-strong);
  border: 1px solid var(--shell-border);
  overflow-x: auto;
  font-family: var(--shell-mono-font);
  font-size: 0.8rem;
  line-height: 1.5;
  white-space: pre-wrap;
}

.job-detail__stalled-note {
  color: #b45309;
  font-size: 0.84rem;
}

.job-detail__retry-input {
  max-width: 180px;
}

.job-detail__conversation {
  margin-top: 16px;
  border: 1px solid var(--shell-border);
  background: var(--shell-surface-strong);
}

.job-detail__conversation-summary {
  padding: 10px 12px;
  cursor: pointer;
  list-style: none;
  font-family: var(--shell-mono-font);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--shell-text-muted);
}

.job-detail__conversation-summary::-webkit-details-marker {
  display: none;
}

.job-detail__conversation-summary::before {
  content: "\25B6";
  display: inline-block;
  margin-right: 8px;
  font-size: 0.55rem;
  transition: transform 0.15s;
}

.job-detail__conversation[open] > .job-detail__conversation-summary::before {
  transform: rotate(90deg);
}

.job-detail__conversation[open] > :not(summary) {
  padding: 0 12px 12px;
}

.job-detail__conversation-body {
  display: grid;
  gap: 12px;
}

.job-detail__conversation-message {
  padding: 12px;
  border: 1px solid var(--shell-border);
  background: var(--shell-surface);
}

.job-detail__conversation-message--assistant {
  border-left: 3px solid var(--status-active);
}

.job-detail__conversation-message--user {
  border-left: 3px solid var(--status-ready);
}

.job-detail__conversation-role {
  margin-bottom: 8px;
  font-weight: 700;
  color: var(--shell-text);
  text-transform: uppercase;
}

.job-detail__subheading {
  margin: 0 0 10px;
}

.job-detail__acceptance-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid var(--shell-border);
  font-size: 0.82rem;
  color: var(--shell-text);
}

.job-detail__acceptance-row:last-child {
  border-bottom: none;
}

.job-detail__diagnostic-list {
  margin: 0;
  padding-left: 18px;
  color: var(--shell-text);
  display: grid;
  gap: 8px;
  font-size: 0.84rem;
}

.job-detail__meta-list {
  display: grid;
  gap: 6px;
  margin: 0;
  font-size: 0.82rem;
}

.job-detail__meta-list dt {
  min-width: 90px;
  font-family: var(--shell-mono-font);
  font-size: 0.72rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--shell-text-muted);
}

.job-detail__meta-list dd {
  margin: 0;
  color: var(--shell-text);
}

.job-detail__mono {
  font-family: var(--shell-mono-font);
  font-size: 0.78rem;
}

.job-detail__delete {
  margin-top: 16px;
  border-color: rgba(156, 48, 48, 0.24);
  color: #9c3030;
  font-size: 0.75rem;
}

.job-detail__message {
  margin: 12px 0 0;
}

@media (max-width: 768px) {
  .dag__phase::after {
    display: none;
  }

  .dag__phase-row,
  .dag__detail-header,
  .job-detail__actions-row,
  .job-detail__subsection-header,
  .job-detail__meta-list div {
    flex-direction: column;
  }

  .dag__detail {
    margin-left: 0;
  }
}
</style>
